<template>
  <div class="bridge-signing-table">
    <v-card-title class="toolbar primary white--text">
      <div>
        <span class="headline">
          <h1 class="headline ml-2">
            <v-icon dark>description</v-icon>
            Exposes
          </h1>
        </span>
      </div>

      <v-spacer />
      <v-text-field
        v-model="search"
        append-icon="search"
        label="Filter results"
        single-line
        flat
        dark
        color="primary"
        solo-inverted
        hide-details
      />
      <v-tooltip
        top
        dark
      >
        <template v-slot:activator="{ on }">
          <v-btn
            color="secondary"
            fab
            dark
            absolute
            bottom
            right
            @click.stop="onCreate"
            v-on="on"
          >
            <v-icon>note_add</v-icon>
          </v-btn>
        </template>
        <span>Create new Expose</span>
      </v-tooltip>

      <v-tooltip
        top
        dark
      >
        <template v-slot:activator="{ on }">
          <v-btn
            color="secondary"
            fab
            dark
            absolute
            bottom
            left
            @click.stop="onRefresh"
            v-on="on"
          >
            <v-icon>refresh</v-icon>
          </v-btn>
        </template>
        <span>Refresh</span>
      </v-tooltip>
    </v-card-title>

    <v-data-table
      :headers="headers"
      :items="filteredItems"
      :pagination.sync="pagination"
      :loading="isLoading"
      loading-text="Loading..."
      disable-initial-sort
    >
      <template
        slot="items"
        slot-scope="props"
      >
        <tr>
          <td class="text-center">
            {{ entityTypes()[props.item.entityType] }}
          </td>
          <td class="text-center">
            {{ props.item.entityId }}
          </td>
          <td class="text-center">
            {{ props.item.projectName }}
          </td>
          <td class="text-center">
            {{ templates()[props.item.templateId] }}
          </td>
          <td class="text-center">
            {{ props.item.status }}
          </td>
          <td class="text-center">
            <DateDisplay
              :date="props.item.createdAt"
              :show-time="true"
            />
          </td>
          <td class="text-center">
            <DateDisplay
              :date="props.item.updatedAt"
              :show-time="true"
            />
          </td>
          <td class="text-center">
            <v-tooltip
              v-if="canShowDraft(props.item)"
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  dark
                  flat
                  icon
                  color="secondary"
                  v-on="on"
                  @click.stop="() => openGoogleDocuments(props.item)"
                >
                  <v-icon>download</v-icon>
                </v-btn>
              </template>
              <span>Show Draft</span>
            </v-tooltip>
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  dark
                  flat
                  icon
                  color="secondary"
                  v-on="on"
                  @click.stop="() => onDebugExpose(props.item)"
                >
                  <v-icon>adb</v-icon>
                </v-btn>
              </template>
              <span>Debug</span>
            </v-tooltip>
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  dark
                  flat
                  icon
                  color="error"
                  v-on="on"
                  @click.stop="() => showDeleteDialog(props.item.id)"
                >
                  <v-icon>delete</v-icon>
                </v-btn>
              </template>
              <span>Delete</span>
            </v-tooltip>
          </td>
        </tr>
      </template>
    </v-data-table>
    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="snackbarTimeout"
    >
      {{ snackBarText }}
      <v-btn
        dark
        flat
        @click="snackbar = false"
      >
        Close
      </v-btn>
    </v-snackbar>
    <CreateDialog
      v-if="isCreating"
      :is-open="isCreating"
      :toggle-open="onCreate"
    />
    <DeleteDialog
      :visible="deleteDialog"
      :expose-id="deleteDialogId"
      @close="hideDeleteDialog()"
      @submit="onDelete(deleteDialogId)"
    />
    <DebugDialog
      :visible="isDebugDialogActive"
      :debug-object="debugObject"
      :title="debugTitle"
      @close="isDebugDialogActive = false"
    />
  </div>
</template>

<script>
import { centsToEuros } from '@/utils/numberFormats';
import DebugDialog from '@/components/payoutsAndTaxes/payout/dialogs/shared/DebugDialog.vue';
import CreateDialog from './dialog/CreateDialog.vue';
import DeleteDialog from './dialog/DeleteDialog.vue';
import DateDisplay from '@/components/payoutsAndTaxes/payout/shared/DateDisplay.vue';

export default {
  name: 'FinancialProductExposeView',
  components: {
    DateDisplay,
    DeleteDialog,
    CreateDialog,
    DebugDialog,
  },
  data() {
    return {
      centsToEuros,
      filterByStatus: 'open',
      isLoading: false,
      pagination: {
        rowsPerPage: 25,
      },
      search: '',
      headers: [
        { text: 'Type', value: 'entityType', align: 'center' },
        { text: 'Type ID', value: 'entityId', align: 'center' },

        { text: 'Project Name', value: 'projectName', align: 'center' },
        { text: 'Template', value: 'templateId', align: 'center' },

        { text: 'Status', value: 'status', align: 'center' },
        { text: 'Created', value: 'createdAt', align: 'center' },
        { text: 'Updated', value: 'updatedAt', align: 'center' },

        { text: 'Actions', sortable: false, align: 'center' },
      ],
      snackbar: false,
      snackbarTimeout: 5000,
      snackbarColor: 'primary',
      snackBarText: '',
      debugObject: {},
      debugTitle: '',
      isDebugDialogActive: false,
      isCreating: false,
      deleteDialog: false,
      deleteDialogId: '',
    };
  },
  computed: {
    list() {
      return this.$store.getters.getExposeList.exposes;
    },
    filteredItems() {
      const { search } = this;

      return search.length < 1 ?
        this.list :
        this.list.filter((item) => {
          let found = false;
          Object.keys(item)
            .map((key) => {
              if (item[key] && String(item[key]).toLowerCase().indexOf(search.toLowerCase()) > -1) {
                found = true;
              }

              return key;
            });

          return found;
        });
    },
  },
  methods: {
    onRefresh() {
      this.$store.dispatch('fetchFpExposeTypes');
      this.$store.dispatch('fetchFpExposeAll').catch((error) => {
        this.snackbarColor = 'red';
        this.snackBarText = `Failed to load list: ${error.message}`;
        this.snackbar = true;
      });
    },
    templates() {
      const data = this.$store.getters.getExposeTypes;
      if (!data || !data.templates) {
        return {};
      }

      return data.templates;
    },
    entityTypes() {
      const data = this.$store.getters.getExposeTypes;
      if (!data || !data.entityTypes) {
        return {};
      }

      return data.entityTypes;
    },
    canShowDraft(expose) {
      if (!expose) {
        return false;
      }

      if (!expose.dcs) {
        return false;
      }

      if (!expose.dcs.draft) {
        return false;
      }

      if (!expose.dcs.draft.remoteId) {
        return false;
      }

      return true;
    },
    openGoogleDocuments(expose, final = false) {
      const remoteId = final
        ? expose.dcs.finalDocument
        : expose.dcs.draft.remoteId;

      if (remoteId) {
        window.open(`https://drive.google.com/open?id=${remoteId}`, '_blank');
      }
    },
    onCreate() {
      this.isCreating = !this.isCreating;
      this.currentTaxTrait = {};
    },
    showDeleteDialog(id) {
      this.deleteDialogId = id;
      this.deleteDialog = true;
      this.$forceUpdate();
    },
    hideDeleteDialog() {
      this.deleteDialog = false;
      this.$forceUpdate();
    },
    onDelete(userId) {
      this.$store
        .dispatch('deleteExpose', userId)
        .then(() => {
          this.hideDeleteDialog();
        })
        .catch((err) => {
          this.error = err;
        });
    },
    onDebugExpose(item) {
      this.debugObject = item;
      this.debugTitle = 'Debug Expose';
      this.isDebugDialogActive = true;
    },
  },
};
</script>
<style lang="scss">
.bridge-signing-table {
  > .toolbar {
    position: relative;
    padding: 24px 24px 32px 24px;
    margin-bottom: 32px;
  }
}

.text-center {
  text-align: center;
}

.mass-create {
  margin-right: 70px;
}
</style>



// WEBPACK FOOTER //
// src/views/financialProducts/FinancialProductExposeView.vue